import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { useMerchant } from '../../merchants/hooks/useMerchants';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import {
  useBookingServices,
  useCreateBookingService,
  useUpdateBookingService,
} from '../hooks/useBookingServices';
import { BookingServiceForm, type BookingServiceFormValues } from '../components/BookingServiceForm';
import type { BookingService } from '../api/bookingServicesApi';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';

function formatFee(service: BookingService): string {
  if (!service.chargingEnabled || !service.fee) return 'Free';
  const symbol = service.currency === 'NGN' ? '₦' : service.currency === 'USD' ? '$' : '';
  return `${symbol}${(service.fee / 100).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MEDIUM_LABELS: Record<string, string> = {
  IN_PERSON: 'In person',
  VIDEO_CALL: 'Video call',
  PHONE: 'Phone',
  ANY: 'Any',
};

export function BookingServicesPage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.BOOKING_SERVICE_MANAGE);

  const { data: merchant } = useMerchant(selectedTenantId ?? '');
  const solutionId = merchant?.solutionId ?? null;

  const { data: services = [], isLoading, error } = useBookingServices(selectedTenantId);
  const createService = useCreateBookingService();
  const updateService = useUpdateBookingService();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BookingService | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (service: BookingService) => {
    setEditing(service);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const isSaving = createService.isPending || updateService.isPending;

  const handleSubmit = async (values: BookingServiceFormValues) => {
    if (editing) {
      await updateService.mutateAsync({ id: editing.id, data: values });
    } else {
      if (!selectedTenantId) return;
      await createService.mutateAsync({
        ...values,
        tenantId: selectedTenantId,
        // booking-service requires solutionId; fall back to rack-and-box if the
        // merchant hasn't loaded yet (matches the platform's default solution).
        solutionId: solutionId ?? 'rack-and-box',
      });
    }
    closeModal();
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">Booking Services</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Manage the bookable services customers can schedule, and whether they are charged.
          </p>
        </div>
        {canManage && selectedTenantId && (
          <Button size="sm" className="h-[40px]" onClick={openCreate}>
            New service
          </Button>
        )}
      </div>

      {!selectedTenantId ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          Select a merchant to manage their booking services.
        </div>
      ) : isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading booking services…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">
          Failed to load booking services. Please try again.
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No booking services yet.
          {canManage && ' Click "New service" to create one.'}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Duration</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Medium</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Fee</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                {canManage && (
                  <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {services.map((service) => (
                <tr key={service.id} className="hover:bg-muted transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{service.title}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{service.durationMins} min</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {MEDIUM_LABELS[service.medium] ?? service.medium}
                  </td>
                  <td className="px-6 py-4 text-sm text-foreground">{formatFee(service)}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${
                        service.isActive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {service.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && (
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openEdit(service)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              {editing ? 'Edit Booking Service' : 'New Booking Service'}
            </h2>
            <button
              onClick={closeModal}
              className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none"
            >
              &times;
            </button>
          </div>
          <BookingServiceForm
            initial={editing}
            onSubmit={handleSubmit}
            onCancel={closeModal}
            isSaving={isSaving}
          />
        </div>
      </Modal>
    </div>
  );
}
